import React, { memo, useState } from "react";
// ... (rest of imports)
import Progress from "../progress";
import { IoArrowUpOutline } from "react-icons/io5";
import { FiMoreVertical, FiSend } from "react-icons/fi";
import { BsPlusCircleFill } from "react-icons/bs";
import { useUpdateTaskOrder } from "../../../api/hooks";
import RecurringBadge, { isRecurringItem } from "../RecurringBadge";
import { formatDate, getMonthKey, formatMonthKeyShort } from "../../../lib/dateUtils";
import { formatDistanceToNow } from "date-fns";

const priorityColors = {
  low: "#00D097", // green
  medium: "#FFBD21", // yellow
  high: "#FF4D4F", // red
};

const UserAvatar = ({ user, size = "w-6 h-6", border = "border-2 border-white", textClass = "text-[10px]" }) => {
  const [error, setError] = useState(false);
  const initials = (user?.firstName?.[0] || user?.email?.[0] || "?").toUpperCase();

  if (!user?.profileImage || error) {
    return (
      <div
        className={`${size} rounded-full ${border} bg-blue-100 flex items-center justify-center ${textClass} font-bold text-blue-600 shrink-0`}
        title={user?.firstName || user?.email}
      >
        {initials}
      </div>
    );
  }

  return (
    <div className={`${size} overflow-hidden rounded-full ${border} shrink-0`} title={user.firstName}>
      <img
        src={user.profileImage}
        alt={user.firstName}
        onError={() => setError(true)}
        loading="lazy"
        className="w-full h-full object-cover"
      />
    </div>
  );
};

const getProgressValue = (status) => {
  switch (status?.toLowerCase()) {
    case "todo":
      return 20;
    case "in-progress":
      return 50;
    case "completed":
      return 100;
    default:
      return 0;
  }
};

const getSentToClientMeta = (task) => {
  if (!task?.sentToClientAt) return null;
  const sender = [task.sentToClientBy?.firstName, task.sentToClientBy?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  let timeAgo = "";
  try {
    timeAgo = formatDistanceToNow(new Date(task.sentToClientAt), {
      addSuffix: true,
    }).replace("about ", "");
  } catch (error) {
    timeAgo = "";
  }
  return {
    sender: sender || "Someone",
    timeAgo,
  };
};

const SentToClientAction = ({
  task,
  onSendToClient,
  isSendingToClient,
  compact = false,
}) => {
  const meta = getSentToClientMeta(task);
  if (meta) {
    return (
      <div
        className={`${compact ? "" : "mt-2"} inline-flex max-w-full items-center gap-1.5 rounded-full bg-teal-100 px-2.5 py-1 text-[11px] font-semibold text-teal-800`}
        title={`Sent to client by ${meta.sender} on ${new Date(
          task.sentToClientAt
        ).toLocaleString()}`}
      >
        <FiSend className="h-3 w-3 shrink-0" />
        <span className="truncate">
          {compact
            ? `${meta.sender}${meta.timeAgo ? ` · ${meta.timeAgo}` : ""}`
            : `Sent to client · ${meta.sender}${meta.timeAgo ? ` · ${meta.timeAgo}` : ""}`}
        </span>
      </div>
    );
  }

  if (!onSendToClient) return null;

  return (
    <button
      type="button"
      disabled={isSendingToClient}
      onClick={(event) => {
        event.stopPropagation();
        onSendToClient(task);
      }}
      className={`${compact ? "" : "mt-2"} inline-flex shrink-0 items-center gap-1.5 rounded-full bg-teal-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60`}
    >
      <FiSend className="h-3 w-3" />
      {isSendingToClient ? "Sending..." : "Send to client"}
    </button>
  );
};

const getStatusStyles = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "todo":
    case "pending":
      return "bg-gray-100 text-gray-600";
    case "in-progress":
      return "bg-blue-50 text-blue-600";
    case "on-review":
      return "bg-violet-50 text-violet-600";
    case "re-work":
      return "bg-rose-50 text-rose-600";
    case "completed":
    case "approved":
    case "client-approved":
      return "bg-[#E0F9F2] text-[#00D097]";
    case "on-hold":
    case "paused":
      return "bg-amber-50 text-amber-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const Task = memo(({
  task,
  onClick,
  isBoardView,
  projectId,
  index,
  onDragStart,
  onDragOver,
  onDrop,
  isMoreOptions,
  onMoreOptions,
  onSendToClient,
  isSendingToClient,
  compact = false,
  // "YYYY-MM" of the month the card is shown under. When given, a card whose due
  // date falls in another month is highlighted (work done ahead of, or after, its month).
  monthKey,
}) => {
  const { mutate: updateOrder } = useUpdateTaskOrder(projectId);
  const priorityColor =
    priorityColors[task?.priority?.toLowerCase()] || priorityColors.medium;
  const progressValue = task?.computedProgress !== undefined ? task.computedProgress : getProgressValue(task?.status);
  const isExtraTask = task?.taskGroup === "extraTask";
  const isSubtaskItem = task?.itemType === "subtask" || task?.type === "subtask";
  const isSentToClient = Boolean(task?.sentToClientAt);
  const compactCardTone = isSentToClient
    ? "bg-teal-50 border-teal-200"
    : "bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50/60";
  const dueMonthKey = getMonthKey(task?.dueDate);
  const isOutsideMonth = Boolean(monthKey && dueMonthKey && dueMonthKey !== monthKey);
  const outsideMonthLabel = isOutsideMonth
    ? `${dueMonthKey > monthKey ? "Next month" : "Earlier month"} · due ${formatMonthKeyShort(dueMonthKey)}`
    : null;
  const outsideMonthBadge = outsideMonthLabel && (
    <span
      className="inline-flex w-fit items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
      title={`Filed under another month: due ${formatDate(task?.dueDate)}`}
    >
      {outsideMonthLabel}
    </span>
  );
  const cardTone = isSentToClient
    ? "bg-teal-50 ring-2 ring-teal-300 border-l-[3px] border-teal-500"
    : isOutsideMonth
      ? "bg-amber-50 ring-1 ring-amber-300 border-l-[3px] border-amber-500"
      : isSubtaskItem
      ? "bg-white border border-slate-200 border-l-[3px] border-l-blue-400"
      : isExtraTask
        ? "bg-white border border-slate-200 border-l-[3px] border-l-purple-500"
        : "bg-white border border-slate-100";
  const hasReporters = !!(
    task?.project?.reporters &&
    task.project.reporters.length > 0 &&
    typeof task.project.reporters[0] === "object" &&
    task.project.reporters[0].firstName
  );

  const getStatusTime = () => {
    let date = null;
    if ((task?.status === "on-review" || task?.status === "approved") && task?.activityLog) {
      const logs = task.activityLog.filter(log => log.changeType === "status_change" && log.newValue === task.status);
      if (logs.length > 0) {
        date = logs[logs.length - 1].createdAt;
      }
    }
    if (!date && task?.updatedAt && (task?.status === "on-review" || task?.status === "approved")) {
      date = task.updatedAt;
    }
    
    if (date) {
      try {
        const timeAgo = formatDistanceToNow(new Date(date), { addSuffix: true });
        return timeAgo.replace('about ', '');
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  const statusTimeAgo = getStatusTime();

  const getOnReviewSubmittedAt = () => {
    if (Array.isArray(task?.activityLog)) {
      const onReviewLogs = task.activityLog.filter(
        (log) =>
          log.changeType === "status_change" &&
          String(log.newValue || "").toLowerCase() === "on-review"
      );
      if (onReviewLogs.length > 0) {
        return onReviewLogs[0].createdAt;
      }
    }
    return task?.scoringCreditedAt || task?.completedAt || null;
  };
  const onReviewSubmittedAt = getOnReviewSubmittedAt();
  const dueDateLabel = formatDate(task?.dueDate);
  const mainTaskDueDate = task?.parentTask?.dueDate;
  const mainTaskDueDateLabel = formatDate(mainTaskDueDate);
  const submittedOnReviewLabel = onReviewSubmittedAt
    ? formatDate(onReviewSubmittedAt)
    : null;
  const isDueOverdue =
    task?.dueDate &&
    !["completed", "approved", "client-approved"].includes(
      String(task?.status || "").toLowerCase()
    ) &&
    new Date(task.dueDate) < new Date(new Date().setHours(0, 0, 0, 0));
  const isMainDueOverdue =
    mainTaskDueDate &&
    new Date(mainTaskDueDate) < new Date(new Date().setHours(0, 0, 0, 0));

  const handleDragStart = (e) => {
    e.dataTransfer.setData("text/plain", task._id);
    onDragStart && onDragStart(e, index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    onDragOver && onDragOver(e, index);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const draggedTaskId = e.dataTransfer.getData("text/plain");
    if (draggedTaskId !== task._id) {
      updateOrder({ taskId: draggedTaskId, newOrder: index });
    }
    onDrop && onDrop(e, index);
  };

  const handleClick = () => {
    if (onClick) {
      onClick(task);
    }
  };

  if (compact) {
    const parentTitle = task?.parentTask?.title;
    return (
      <div
        onClick={() => handleClick()}
        className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 px-4 py-3 rounded-2xl border cursor-pointer transition-colors ${compactCardTone}`}
      >
        {(task?.project?.thumbImg || task?.project?.name || task?.project?.displayName) && (
          <div className="shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400">
            {task.project?.thumbImg ? (
              <img 
                src={task.project.thumbImg} 
                alt="Project" 
                className="w-full h-full object-contain p-1" 
              />
            ) : (
              <div className="flex items-center justify-center w-full h-full bg-blue-50/50">
                <span className="text-lg sm:text-xl font-bold text-blue-300">
                  {(task.project?.name || task.project?.displayName || "?").charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <h4 className="font-medium text-[#0A1629] truncate">
              {task?.title}
            </h4>
            {isSubtaskItem && (
              <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600">
                Subtask
              </span>
            )}
            {isExtraTask && (
              <span className="shrink-0 rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-600">
                Extra
              </span>
            )}
            {isRecurringItem(task) && <RecurringBadge />}
          </div>
          <p className="mt-0.5 truncate text-xs text-gray-500">
            {task?.project?.name || task?.project?.displayName || "Extra task"}
            {parentTitle ? ` · ${parentTitle}` : ""}
            {hasReporters
              ? ` · ${task.project.reporters.map((r) => r.firstName).join(", ")}`
              : ""}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
            <span
              className={`inline-flex items-center gap-1 font-medium ${
                isDueOverdue ? "text-rose-600" : "text-gray-600"
              }`}
              title={task?.dueDate ? new Date(task.dueDate).toLocaleString() : "No deadline"}
            >
              <span className="text-gray-400 font-semibold uppercase tracking-wide text-[10px]">
                Deadline
              </span>
              {dueDateLabel || "—"}
            </span>
            {isSubtaskItem && (
              <span
                className={`inline-flex items-center gap-1 font-medium ${
                  isMainDueOverdue ? "text-rose-600" : "text-gray-600"
                }`}
                title={
                  mainTaskDueDate
                    ? `Main task deadline: ${new Date(mainTaskDueDate).toLocaleString()}`
                    : "No main task deadline"
                }
              >
                <span className="text-gray-400 font-semibold uppercase tracking-wide text-[10px]">
                  Main task
                </span>
                {mainTaskDueDateLabel || "—"}
              </span>
            )}
            {task?.status === "on-review" && (
              <span
                className="inline-flex items-center gap-1 font-medium text-violet-700"
                title={
                  onReviewSubmittedAt
                    ? new Date(onReviewSubmittedAt).toLocaleString()
                    : "Not submitted for review yet"
                }
              >
                <span className="text-violet-400 font-semibold uppercase tracking-wide text-[10px]">
                  On review
                </span>
                {submittedOnReviewLabel || "—"}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-4 shrink-0 self-stretch sm:self-auto">
          <div className="hidden md:flex items-center shrink-0">
            {task?.assignedTo?.length > 0 ? (
              <div className="flex -space-x-1">
                {task.assignedTo.slice(0, 3).map((user, index) => (
                  <UserAvatar key={user._id || index} user={user} />
                ))}
                {task.assignedTo.length > 3 && (
                  <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-100 text-[10px] flexCenter font-medium text-gray-600 shrink-0">
                    +{task.assignedTo.length - 3}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-6 h-6 rounded-full bg-gray-100 flexCenter">
                <span className="text-xs text-gray-400">—</span>
              </div>
            )}
          </div>

          <div
            className="hidden lg:flex items-center gap-1 shrink-0 w-20"
            style={{ color: priorityColor }}
          >
            <IoArrowUpOutline className="text-sm" />
            <span className="text-xs font-medium">{task?.priority}</span>
          </div>

          <span className={`hidden md:inline-flex shrink-0 rounded-lg px-2.5 py-1 text-[11px] font-medium capitalize ${getStatusStyles(task?.status)}`}>
            {task?.status}
          </span>

          <SentToClientAction
            task={task}
            onSendToClient={onSendToClient}
            isSendingToClient={isSendingToClient}
            compact
          />

          {isMoreOptions && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onMoreOptions && onMoreOptions(task, event);
              }}
              className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 shrink-0"
              title="More options"
            >
              <FiMoreVertical size={18} />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isBoardView) {
    return (
      <div
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => handleClick()}
        title={task?.itemType === "subtask" ? "Click to view parent task" : ""}
        className={`p-4 cursor-grab rounded-lg shadow-sm hover:shadow-md 
          transition-shadow relative ${cardTone}`}
      >
        {/* Floating Badges removed for cleaner UI */}

        {/* Extra Task Badge */}
        {isExtraTask && (
          <div className="absolute -top-2 -right-2 bg-purple-500 text-white rounded-full p-1">
            <BsPlusCircleFill className="text-xs" />
          </div>
        )}

        {/* Board Task Badge */}
        {task?.isBoardTask &&
          !task?.project &&
          !isExtraTask &&
          !task?.itemType === "subtask" && (
            <div className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full p-1">
              <span className="text-xs">🎯</span>
            </div>
          )}

        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <h4
                className={`font-medium line-clamp-2 ${task?.itemType === "subtask"
                  ? "text-slate-800"
                  : isExtraTask
                    ? "text-slate-800"
                    : "text-slate-800"
                  }`}
              >
                {task?.title}
              </h4>
              {isRecurringItem(task) && (
                <div>
                  <RecurringBadge />
                </div>
              )}
              {outsideMonthBadge}
              {task?.itemType === "subtask" ? (
                <div className="flex flex-col gap-1.5 mt-1">
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide border border-blue-100/50">
                      Subtask
                    </span>
                  </div>
                  {task?.parentTask && (
                    <span className="text-[11px] font-medium text-slate-500 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      <span className="truncate max-w-[190px]" title={task.parentTask.title}>{task.parentTask.title}</span>
                    </span>
                  )}
                </div>
              ) : task?.project ? (
                <span className="text-xs text-gray-500">
                  📋 {task.project.name || task.project.displayName}
                  {hasReporters && ` • Reporter: ${task.project.reporters.map(r => r.firstName).join(", ")}`}
                </span>
              ) : task?.isBoardTask ? (
                <span className="text-xs text-blue-500">🎯 Board Task</span>
              ) : null}
            </div>
            {isExtraTask && (
              <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full font-medium ml-2">
                Extra
              </span>
            )}
          </div>

          {/* Extra Task Work Type */}
          {isExtraTask && task?.extraTaskWorkType && (
            <div className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded capitalize">
              📌 {task.extraTaskWorkType} work
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Multiple assignees display */}
              {task?.assignedTo?.length > 0 ? (
                <div className="flex -space-x-1">
                  {task.assignedTo.slice(0, 3).map((user, index) => (
                    <UserAvatar key={user._id || index} user={user} />
                  ))}
                  {task.assignedTo.length > 3 && (
                    <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 text-[10px] flexCenter font-medium text-gray-600 shrink-0">
                      +{task.assignedTo.length - 3}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-6 h-6 rounded-full bg-gray-200 flexCenter">
                  <span className="text-xs text-gray-500">?</span>
                </div>
              )}
              <div
                className="flex items-center gap-1"
                style={{ color: priorityColor }}
              >
                <IoArrowUpOutline className="text-sm" />
                <span className="text-xs font-medium">{task?.priority}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
              {progressValue}%
              <Progress size={24} strokeWidth={2} currentValue={progressValue} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => handleClick()}
      title={task?.itemType === "subtask" ? "Click to view parent task" : ""}
      className={`grid gap-y-5 md:gap-y-0 grid-cols-1 md:grid-cols-10 cursor-pointer md:gap-x-3
         px-5 py-5 rounded-3xl relative ${cardTone}`}
    >
      {/* Subtask Badge for List View */}
      {task?.itemType === "subtask" && (
        <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full p-1">
          <span className="text-xs">📋</span>
        </div>
      )}

      {/* Extra Task Badge for List View */}
      {isExtraTask && (
        <div className="absolute -top-1 -right-1 bg-purple-500 text-white rounded-full p-1">
          <BsPlusCircleFill className="text-xs" />
        </div>
      )}

      {/* Board Task Badge for List View */}
      {task?.isBoardTask &&
        !task?.project &&
        !isExtraTask &&
        !task?.itemType === "subtask" && (
          <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full p-1">
            <span className="text-xs">🎯</span>
          </div>
        )}

      <div
        className="col-span-1  border-b pb-6 border-gray-200 md:border-none
      md:pb-0 md:col-span-3 flexBetween"
      >
        <div className="flex flex-col gap-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#91929E]">Task Name</span>
            {task?.itemType === "subtask" && (
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                Subtask
              </span>
            )}
            {task?.isPublishPending && (
              <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium">
                Ready to Publish
              </span>
            )}
            {isExtraTask && (
              <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium">
                Extra
              </span>
            )}
            {isSentToClient && (
              <span className="bg-teal-100 text-teal-700 text-xs px-2 py-0.5 rounded-full font-medium">
                Sent to client
              </span>
            )}
            {outsideMonthBadge}
            {task?.isBoardTask &&
              !task?.project &&
              !task?.itemType === "subtask" && (
                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  Board
                </span>
              )}
          </div>
          <h4
            className={
              task?.itemType === "subtask"
                ? "text-blue-800 font-medium"
                : isExtraTask
                  ? "text-purple-800 font-medium"
                  : ""
            }
          >
            {task?.title.slice(0, 35)}
            {task?.title.length > 35 && "..."}
          </h4>
          {/* Project info for tasks/subtasks */}
          {task?.project?.name && (
            <div className="text-xs bg-gray-50 px-3 py-1 rounded-full text-gray-500 mt-1">
              <span>{task.project.name}</span>
              {hasReporters && (
                <span className="text-gray-400">
                  {" • Reporter: " + task.project.reporters.map(r => r.firstName).join(", ")}
                </span>
              )}
            </div>
          )}
          <SentToClientAction
            task={task}
            onSendToClient={onSendToClient}
            isSendingToClient={isSendingToClient}
          />
        </div>

        <div className="visible md:hidden">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-700">{progressValue}%</span>
            <Progress size={30} strokeWidth={2} currentValue={progressValue} />
          </div>
        </div>

        {/* Parent Task Info for Subtasks in List View */}
        {task?.itemType === "subtask" && task?.parentTask && (
          <div className="text-xs text-blue-600 mt-1">
            📋 Parent: {task.parentTask.title}
          </div>
        )}
        {/* Extra Task Work Type in List View */}
        {isExtraTask && task?.extraTaskWorkType && (
          <div className="text-xs text-purple-600 mt-1">
            📌 {task.extraTaskWorkType} work
          </div>
        )}
      </div>
      <div className="col-span-1 md:col-span-5 grid grid-cols-4">
        <div className="flex flex-col gap-y-1">
          <span className="text-sm text-[#91929E]">Estimate</span>
          <h4 className="truncate">{task?.timeEstimate}h</h4>
        </div>
        <div className="flex flex-col gap-y-1">
          <span className="text-sm text-[#91929E]">Due Date</span>
          <h4 className={`text-sm font-medium truncate ${isDueOverdue ? "text-rose-600" : ""}`}>
            {formatDate(task?.dueDate) || "—"}
          </h4>
          {submittedOnReviewLabel && task?.status === "on-review" && (
            <span
              className="text-[11px] text-violet-600 font-medium"
              title={
                onReviewSubmittedAt
                  ? new Date(onReviewSubmittedAt).toLocaleString()
                  : undefined
              }
            >
              On review {submittedOnReviewLabel}
            </span>
          )}
          {!submittedOnReviewLabel &&
            (task?.status === "on-review" || task?.status === "approved") &&
            statusTimeAgo && (
              <span className="text-[11px] text-gray-500 capitalize -mt-1 font-medium">
                {statusTimeAgo}
              </span>
            )}
        </div>
        <div className="flex flex-col gap-y-1">
          <span className="text-sm text-[#91929E]">Assignees</span>
          {task?.assignedTo?.length > 0 ? (
            <div className="flex items-center gap-1">
              <div className="flex -space-x-1">
                {task.assignedTo.slice(0, 2).map((user, index) => (
                  <UserAvatar
                    key={user._id || index}
                    user={user}
                    border="border border-white"
                  />
                ))}
              </div>
              {task.assignedTo.length > 2 && (
                <span className="text-xs text-gray-500">
                  +{task.assignedTo.length - 2}
                </span>
              )}
            </div>
          ) : (
            <div className="w-6 h-6 rounded-full bg-gray-200 flexCenter">
              <span className="text-xs text-gray-500">?</span>
            </div>
          )}
        </div>
        <div className="md:flex hidden flex-col gap-y-1">
          <span className="text-sm text-[#91929E]">Priority</span>
          <div className="flexStart gap-x-1" style={{ color: priorityColor }}>
            <IoArrowUpOutline className="text-lg " />
            <span className="text-xs font-medium">{task?.priority}</span>
          </div>
        </div>
      </div>
      <div className="  col-span-1 md:col-span-2  flexBetween">
        <div className="flex md:hidden flex-col gap-y-1">
          <span className="text-sm text-[#91929E]">Priority</span>
          <div className="flexStart gap-x-1" style={{ color: priorityColor }}>
            <IoArrowUpOutline className="text-lg " />
            <span className="text-xs font-medium">{task?.priority}</span>
          </div>
        </div>
        <span
          className={`flexCenter capitalize text-xs font-medium py-[7px] px-[15px] rounded-lg ${getStatusStyles(task?.status)}`}
        >
          {task?.status}
        </span>
        <div className="md:flex hidden">
          {isMoreOptions ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoreOptions && onMoreOptions(task, e);
              }}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FiMoreVertical size={20} className="text-gray-500" />
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-700">{progressValue}%</span>
              <Progress size={30} strokeWidth={2} currentValue={progressValue} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default Task;
